import React, { useEffect, useState, FC } from "react";
import { useTranslation } from "react-i18next";
import {
  RadioChangeEvent,
  RadioGroup,
  SelectInput,
} from "@life-line-lab/components-react";
import { omitBy, isEmpty, isNil } from "lodash";
import errorHandler from "Utils/error-handler";
import { ParcelInfoSchema } from "./parcel-info-validation";
import { Col, Row } from "antd";
import { Button } from "@life-line-lab/components-react";
import ParcelInfoBundle from "./parcel-info-bundle";
import {
  HasMultipleGoods,
  ParcelStatusByPage,
  ParcelTypeEnums,
} from "Data/order";

interface ParcelInfo {
  onPrev?: any;
  onNext?: any;
  handleSubmit?: any;
  goodCareConditionList: any;
  parentInput?: any;
  isEdit?: any;
  current?: any;
}

const initialGoods = {
  typeOfGood: "",
  goodsValue: "",
  trackingNumber: "",
};

const initialParcel = {
  parcelNumber: 0,
  goods: [initialGoods],
};

// Each order contains multiple parcels
// Each parcel contains multiple goods
const initialInput = {
  hasInsurance: "NO",
  parcelType: ParcelTypeEnums.INDIVIDUAL,
  parcels: [initialParcel],
};

const initialError = {};

const insuranceOptions = [
  {
    label: "Yes",
    value: "YES",
  },
  {
    label: "No",
    value: "NO",
  },
];

const parcelTypeOptions = Object.values(ParcelTypeEnums);

const useParcelInfo = (props) => {
  const { TYPE_OF_GOODS = [] } = props || {};

  const { t } = useTranslation();

  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | RadioChangeEvent,
  ) => {
    setError({});

    const { name, value } = e.target;

    // for radio name checking
    if (!name) {
      console.log("Error: input has no name.");
      return;
    }

    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({});

    // if the user is changing parcelType, reset the fields
    if (name === "parcelType") {
      setInput((prev) => ({ ...initialInput, [name]: value }));
      return;
    }

    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleArrTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: {
      parentIndex: number;
      childIndex?: number;
    },
  ) => {
    setError({});

    if (!index) return;
    if (isNil(index.parentIndex)) return;

    const { name, value } = e.target;
    const { parentIndex, childIndex } = index;

    // if there is no childIndex, the fields in the parentIndex is being changed
    if (isNil(childIndex)) {
      const newParcel = {
        ...input.parcels[parentIndex],
        [name]: value,
      };

      setInput((prev) => ({
        ...prev,
        parcels: prev.parcels.map((p, i) =>
          i === parentIndex ? newParcel : p,
        ),
      }));

      return;
    }

    // otherwise, make the changes in that child
    try {
      const newGoods = {
        ...input.parcels[parentIndex].goods[childIndex],
        [name]: value,
      };

      const newParcel = {
        ...input.parcels[parentIndex],
        goods: input.parcels[parentIndex].goods.map((g, i) =>
          i === childIndex ? newGoods : g,
        ),
      };

      setInput((prev) => ({
        ...prev,
        parcels: prev.parcels.map((p, i) =>
          i === parentIndex ? newParcel : p,
        ),
      }));
    } catch (e) {
      console.log(`Error: parcel ${parentIndex} has invalid goods.`, e);
    }
  };

  const handleArrSelectChange = (name, value, index) => {
    setError(initialError);

    const { parentIndex, childIndex } = index;

    // find the name of the goods selected
    let otherFields: any = {};

    if (name === "typeOfGood") {
      const goodsDetails = TYPE_OF_GOODS.find((t) => t.id === value);

      if (goodsDetails) {
        otherFields = { name: goodsDetails?.name };
      }
    }

    // if there is no childIndex, the fields in the parentIndex is being changed
    if (isNil(childIndex)) {
      const newParcel = {
        ...input.parcels[parentIndex],
        ...otherFields,
        [name]: value,
      };

      setInput((prev) => ({
        ...prev,
        parcels: prev.parcels.map((p, i) =>
          i === parentIndex ? newParcel : p,
        ),
      }));

      return;
    }

    // otherwise, make the changes in that child
    try {
      const newGoods = {
        ...input.parcels[parentIndex].goods[childIndex],
        ...otherFields,
        [name]: value,
      };

      const newParcel = {
        ...input.parcels[parentIndex],
        goods: input.parcels[parentIndex].goods.map((g, i) =>
          i === childIndex ? newGoods : g,
        ),
      };

      setInput((prev) => ({
        ...prev,
        parcels: prev.parcels.map((p, i) =>
          i === parentIndex ? newParcel : p,
        ),
      }));
    } catch (e) {
      console.log(`Error: parcel ${parentIndex} has invalid goods.`, e);
    }
  };

  const handleSwitchChange = (value: boolean, name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  // Add a parcel
  // -> Only applicable for split orders
  const handleAddParcel = () => {
    setError({});
    if (input.parcelType !== ParcelTypeEnums.SPLIT) {
      return;
    }

    const newParcel = {
      ...initialParcel,
      parcelNumber: input.parcels.length,
    };

    setInput((prev) => ({
      ...prev,
      parcels: [...prev.parcels, newParcel],
    }));
  };

  // Delete a parcel
  // -> Only applicable for split orders
  const handleDeleteParcel = (index: number) => {
    setError({});
    if (isNil(index)) return;

    if (input.parcelType !== ParcelTypeEnums.SPLIT) {
      return;
    }

    if (input.parcels.length < 2) return;

    if (confirm(t("txt.confirmDeleteParcel"))) {
      const filteredParcels = input.parcels.filter(
        (_: any, parcelIndex: number) => parcelIndex !== index
      );

      setInput((prev) => ({
        ...prev,
        parcels: filteredParcels,
      }));
    }
  };

  const handleAddGoods = (parcelIndex: number) => {
    setError({});
    if (isNil(parcelIndex)) return;

    try {
      const newParcel = {
        ...input.parcels[parcelIndex],
        goods: input.parcels[parcelIndex].goods.concat(initialGoods),
      };

      setInput((prev) => ({
        ...prev,
        parcels: prev.parcels.map((p, i) =>
          i === parcelIndex ? newParcel : p,
        ),
      }));
    } catch (e) {
      console.log(`Error: parcel ${parcelIndex} has invalid goods.`, e);
    }
  };

  const handleDeleteGoods = (parcelIndex: number, goodsIndex: number) => {
    setError({});
    if (isNil(parcelIndex)) return;
    if (isNil(goodsIndex)) return;

    try {
      const newParcel = {
        ...input.parcels[parcelIndex],
        goods: input.parcels[parcelIndex].goods.filter(
          (g, i) => i !== goodsIndex,
        ),
      };

      setInput((prev) => ({
        ...prev,
        parcels: prev.parcels.map((p, i) =>
          i === parcelIndex ? newParcel : p,
        ),
      }));
    } catch (e) {
      console.log(`Error: parcel ${parcelIndex} has invalid goods.`, e);
    }
  };

  return {
    input,
    setInput,
    error,
    setError,
    handleChange,
    handleSelectChange,
    handleArrTextChange,
    handleArrSelectChange,
    handleSwitchChange,
    handleAddParcel,
    handleDeleteParcel,
    handleAddGoods,
    handleDeleteGoods,
  };
};

/**
 * There are three types of orders (parcel type):
 * INDIVIDUAL: Has one parcel, can contain one goods.
 * BULK: Has one parcel, can contain multiple goods.
 * SPLIT: Has multiple parcels, each can contain multiple goods.
 */
const ParcelInfo: FC<ParcelInfo> = ({
  onPrev,
  onNext,
  handleSubmit,
  goodCareConditionList,
  parentInput,
  isEdit,
  current,
}) => {
  const { t } = useTranslation();

  const { TYPE_OF_GOODS = [] } = goodCareConditionList || {};

  const {
    input,
    setInput,
    error,
    setError,
    handleChange,
    handleSelectChange,
    handleArrTextChange,
    handleArrSelectChange,
    handleSwitchChange,
    handleAddParcel,
    handleDeleteParcel,
    handleAddGoods,
    handleDeleteGoods,
  } = useParcelInfo({ TYPE_OF_GOODS });

  useEffect(() => {
    if (!isEmpty(parentInput)) {
      const isSplitShipment = parentInput.parcelType === ParcelTypeEnums.SPLIT;

      let parcels: any[] = parentInput.parcels;

      // EDIT USE CASE :
      // we need to remap the parcels from parcels back to parent for the remarks.
      // remarks is in the parcel page but when edit, variable is in the parent level
      if (isEdit) {
        const createParcel = (data: any, isChildOrder: boolean) => {
          const baseParcel = {
            _id: isChildOrder ? data._id : "",
            goods: isChildOrder ? data.parcels : data.goods,
            height: isChildOrder ? data.dimension.height : data.height,
            length: isChildOrder ? data.dimension.length : data.length,
            weight: data.weight,
            width: isChildOrder ? data.dimension.width : data.width,
            remarks: isChildOrder ? data.remarks : parentInput.remarks,
          };

          return omitBy(baseParcel, isEmpty);
        };

        const sourceData = isSplitShipment
          ? parentInput.childrenOrdersData
          : parentInput.parcels;

        parcels = sourceData.map((item: any) =>
          createParcel(item, isSplitShipment),
        );
      }

      setInput({
        ...initialInput,
        // note: due to changing requirements, hasInsurance is a boolean but client wants Radio
        // hasInsurance as a string starts here, and ends here
        parcelType: parentInput?.parcelType || initialInput.parcelType,
        hasInsurance:
          parentInput.hasInsurance === "YES" || initialInput.hasInsurance,
        parcels: parcels || initialInput.parcels,
      });
    }
  }, [parentInput]);

  const handleSubmitInfo = () => {
    try {
      const valInput = {
        ...input,
        // parcel: input.parcels,
        hasInsurance: input.hasInsurance === "YES",
      };

      const value: any = ParcelInfoSchema.validateSync(valInput, {
        context: {
          HasMultipleGoods: HasMultipleGoods.includes(input.parcelType),
        },
      });

      if (value) {
        handleSubmit(value);
        onNext(value);
      }
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);

      console.log(path, value);

      // get the value after . if any
      const extractedPath = path.substring(path.lastIndexOf(".") + 1);

      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${extractedPath}`) }),
      });
    }
  };

  const parcelLength = input.parcels.length;

  // we DONT allow to ADD or DELETE split shipments order when:
  // parcel has passed SORTING PHASE
  const isSplitShipmentAllowAddDelete = () => {
    const isSplitShipment = input.parcelType === ParcelTypeEnums.SPLIT;
    const orderStatus = parentInput.orderStatusInfo
      ? parentInput.orderStatusInfo.orderStatus
      : null;

    const validStatus = [
      ...ParcelStatusByPage.DIOLKO_PICKUP,
      ...ParcelStatusByPage.DIOLKO_REVIEW,
      ...ParcelStatusByPage.CUSTOMER_CONFIRMATION,
      ...ParcelStatusByPage.DISPATCH_READY,
      ...ParcelStatusByPage.CUSTOMER_DROPOFF,
    ].includes(orderStatus);

    return isSplitShipment && (!isEdit || (isEdit && validStatus));
  };

  const bundleProps = {
    input,
    error,
    handleChange,
    handleSelectChange,
    goodCareConditionList,
    handleArrSelectChange,
    handleArrTextChange,
    onDeleteParcel: handleDeleteParcel,
    handleAddGoods,
    handleDeleteGoods,
    parcelLength,
    isSplitShipmentAllowAddDelete,
  };

  return (
    <div className="om-ocf-pi ocf-pi-container">
      <div className="center header-title">
        {t("title.parcelInfo").toUpperCase()}
      </div>
      <div className="ocf-pi-container-inner">
        <Row gutter={[16, 16]}>
          <Col span={24} lg={12}>
            <SelectInput
              options={parcelTypeOptions.map((p) => ({
                label: t(`option.${p}`),
                value: p,
              }))}
              value={input?.parcelType}
              onChange={(value) => handleSelectChange(value, "parcelType")}
              isRequired={true}
              label={t("label.parcelType")}
            />
          </Col>
          <Col span={24} lg={12}>
            <RadioGroup
              options={insuranceOptions}
              direction="horizontal"
              value={input?.hasInsurance}
              label={t("label.additionalInsurance")}
              name="hasInsurance"
              onChange={handleChange}
            />
          </Col>
        </Row>
        <div className="ocf-pi-content">
          {input.parcels.map((p, i) => {
            const goodsLength = p.goods.length;

            const parcelProps = {
              parentIndex: i,
              goodsLength,
              parcelInput: p,
            };

            return (
              <div className="ocf-pi-parcel" key={i}>
                <ParcelInfoBundle {...bundleProps} {...parcelProps} />
              </div>
            );
          })}
        </div>
      </div>
      {isSplitShipmentAllowAddDelete() && (
        <div className="ocf-pih-btn-container">
          <Button
            label={t("action.addParcel")}
            onClick={() => handleAddParcel()}
          />
        </div>
      )}
      <div className="logistics-divider"></div>
      {!isEmpty(error) && (
        <div className="u-info u-error">{t("error.pleaseResolveErrors")}</div>
      )}
      <div className="steps-action center">
        {current > 0 && (
          <Button
            onClick={() => onPrev(input)}
            type="ghost"
            label={t("action.back")}
            className="mr-10"
          />
        )}
        <Button
          type="primary"
          onClick={() => handleSubmitInfo()}
          label={t("action.next")}
        />
      </div>
    </div>
  );
};

export default ParcelInfo;
